// ReviewRecordTable.js - With Loader + Cache
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllAudioBuzzRecords, deleteAudioRecord } from '../../services/apiService';
import Swal from 'sweetalert2';

const Loader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-900">
    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const ReviewRecordTable = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  
  const getDeletedIds = () => {
    const stored = localStorage.getItem('deletedAudioBuzzIds');
    return stored ? JSON.parse(stored) : [];
  };

  const addDeletedId = (id) => {
    const deletedIds = getDeletedIds();
    if (!deletedIds.includes(id)) {
      deletedIds.push(id);
      localStorage.setItem('deletedAudioBuzzIds', JSON.stringify(deletedIds));
    }
  };

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        const result = await getAllAudioBuzzRecords(); // uses cache inside service
        if (result.success && result.data) {
          const deletedIds = getDeletedIds();
          const filteredRecords = result.data.filter(record => !deletedIds.includes(record._id));
          setRecords(filteredRecords);
        }
      } catch (err) {
        console.error('Error fetching records:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, []);

  const handleBackClick = () => {
    navigate('/audiobuzzManagement');
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

const getTags = (tagsString) => {
  if (!tagsString) return [];
  
  // Split "Audio Buzz Corrected" into two separate tags
  if (tagsString === 'Audio Buzz Corrected') {
    return ['Audio Buzz', 'Corrected'];
  }
  
  // Split "No Buzz Corrected" into two separate tags
  if (tagsString === 'No Buzz Corrected') {
    return ['No Buzz', 'Corrected'];
  }
  
  // Return other tags as-is
  return [tagsString];
};

const getTagClass = (tag) => {
  // Exact colors from your design image
  const tagClasses = {
    'Audio Buzz': 'bg-red-700 text-white',      // Red color
    'Corrected': 'bg-orange-600 text-white',     // Orange color
    'No Buzz': 'bg-green-600 text-white'         // Green color
  };
  
  return tagClasses[tag] || 'bg-gray-600 text-white';
};



  const totalPages = Math.max(1, Math.ceil(records.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const visibleRecords = records.slice(startIndex, startIndex + pageSize);

  const prevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const nextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));
  const paginate = (pageNum) =>
    setCurrentPage(() => Math.min(Math.max(1, pageNum), totalPages));

  const handleDelete = async (id) => {
  const result = await Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this record? This action cannot be undone.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it!'
  });

  if (result.isConfirmed) {
    try {
      await deleteAudioRecord(id);
      setRecords((prevRecords) => prevRecords.filter((record) => record._id !== id));
      addDeletedId(id);
      if (currentPage > Math.max(1, Math.ceil((records.length - 1) / pageSize))) {
        setCurrentPage(1);
      }
      Swal.fire(
        'Deleted!',
        'The record has been deleted.',
        'success'
      );
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'Failed to delete the record. Please try again.',
      });
    }
  }
};

  const handleEnter = (record) => {
    const audioIds = Object.values(record.audio_recording_interface || {});
    navigate('/audioSpectrogram', {
      state: {
        recordId: record._id,
        audioIds,
        modelName:
          record.mic_info?.audio_interface,
      },
    });
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="bg-gray-900 text-white min-h-screen p-4 flex items-center justify-center">
        <div className="text-xl text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen p-4 pb-10">
      <div className="mb-6 flex items-center gap-3">
        <ArrowLeft
          className="w-6 h-6 text-white cursor-pointer"
          onClick={handleBackClick}
        />
        <h1 className="sm:text-2xl text-md font-bold">
          Review Record and Download
        </h1>
      </div>

      <div className="sm:mb-6 mb-4">
        <h1 className="sm:text-2xl text-md font-bold text-left">
          Records ({records.length})
        </h1>
      </div>

      <div className="overflow-x-auto shadow-lg rounded-lg">
        <table className="w-full bg-gray-800 border-collapse table-auto mb-5">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-6 px-3 sm:px-4 font-semibold text-sm sm:text-base whitespace-nowrap">
                Model Name
              </th>
              <th className="text-left py-6 px-3 sm:px-4 font-semibold text-sm sm:text-base whitespace-nowrap">
                Uploader
              </th>
              <th className="text-left py-6 px-3 sm:px-4 font-semibold text-sm sm:text-base whitespace-nowrap">
                Criteria
              </th>
              <th className="text-left py-6 px-3 sm:px-4 font-semibold text-sm sm:text-base whitespace-nowrap">
                OSD Volume
              </th>
              <th className="text-left py-6 px-3 sm:px-4 font-semibold text-sm sm:text-base whitespace-nowrap">
                Tag
              </th>
              <th className="text-left py-6 px-3 sm:px-4 font-semibold text-sm sm:text-base whitespace-nowrap">
                Timestamp
              </th>
              <th className="text-left py-6 px-3 sm:px-4 font-semibold text-sm sm:text-base whitespace-nowrap">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRecords.map((record) => (
              <tr
                key={record._id}
                className="border-b border-gray-700 hover:bg-gray-700 transition-colors"
              >
                <td className="py-2 sm:py-6 px-3 sm:px-4 text-sm sm:text-md whitespace-nowrap">
                  { record.mic_info?.audio_interface || 'N/A'}
                </td>
                <td className="py-2 sm:py-6 px-3 sm:px-4 text-sm sm:text-md whitespace-nowrap">
                  {record.uploadedBy || 'N/A'}
                </td>
                <td className="py-2 sm:py-6 px-3 sm:px-4 text-sm sm:text-md whitespace-nowrap">
                  {record.criteria || record.dut_info?.spl_criteria || 'N/A'}
                </td>
                <td className="py-2 sm:py-6 px-3 sm:px-4 text-sm sm:text-md whitespace-nowrap">
                  {record.osd_volume || record.osd_volume_setting?.level || 'N/A'}
                </td>
                <td className="py-2 sm:py-6 px-3 sm:px-4 text-sm sm:text-md whitespace-nowrap">
                  <div className="flex flex-wrap gap-2 items-center">
                    {getTags(record.tags).map((tag, tagIndex) => (
                      <span 
                        key={tagIndex} 
                        className={`inline-block px-3 py-1 rounded-md text-xs sm:text-sm font-semibold whitespace-nowrap ${getTagClass(tag)}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </td>

                <td className="py-1 sm:py-3 px-3 sm:px-4 text-sm text-gray-300 whitespace-nowrap">
                  {formatTimestamp(record.createdAt)}
                </td>
                <td className="py-1 sm:py-3 px-3 sm:px-4">
                  <div className="flex gap-8">
                  <button
                    onClick={() => handleEnter(record)}
                    className="bg-gray-800 text-blue-600 font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded text-sm sm:text-md flex items-center gap-1"
                  >
                    Enter
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button
                  onClick={() => handleDelete(record._id)}
                  className="bg-gray-800 text-red-600 font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded text-base sm:text-md flex items-center gap-1 hover:bg-slate-900"
                >
                  Delete
                  <span className="bg-red-600 rounded p-0.5 sm:p-1 flex items-center justify-center">
                  <X className="w-4 h-4 sm:w-4 sm:h-4 text-white font-bold" />
                  </span>
                </button>
                </div>
                </td>
              </tr>
            ))}

            {visibleRecords.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="py-3 px-4 text-center text-md text-gray-300"
                >
                  No records to display.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* bottom pagination (unchanged) */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800/95 backdrop-blur-sm z-50 border-t border-slate-500">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center py-4 space-x-4">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`border border-gray-700 rounded-md p-1 transition-all ${
                currentPage === 1
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-700 hover:border-gray-600'
              }`}
            >
              <ChevronLeft className="h-5 w-5 text-gray-400" />
            </button>

            <div className="flex space-x-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                if (pageNum < 1 || pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      currentPage === pageNum
                        ? 'bg-blue-900 text-blue-300 shadow-lg shadow-blue-900/50'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`border border-gray-700 rounded-md p-1 transition-all ${
                currentPage === totalPages || totalPages === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-700 hover:border-gray-600'
              }`}
            >
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewRecordTable;
